import { element } from './definition';
import { named, num, text, toggle } from '../../themes/settingFields';
import { look, paint } from './styleTokens';

// Product content is scoped to a chosen record; its child elements remain ordinary editable nodes.
export const productScopeElement = element('product-scope', {
  label: 'Product', description: 'Choose a product, then design its image, title, price and purchase controls.',
  category: 'Other', icon: 'shopping-bag',
  props: [{ key: 'product', label: 'Product', control: 'product' }, toggle('sampleContent', 'Show theme samples when empty')],
  defaults: { props: { product: '' }, styles: {} }
});

// Purchase owns variant selection and quantity, while the surrounding card owns its visual elements.
export const purchaseElement = element('purchase', {
  label: 'Purchase button', description: 'Choose options and add this product to the checkout.',
  category: 'Other', icon: 'shopping-bag',
  props: [
    text('label', 'Button text'), toggle('showOptions', 'Show variant and quantity controls'),
    named('optionStyle', 'Options', [['dropdown', 'One dropdown'], ['buttons', 'Buttons for each choice']]),
    toggle('stepper', 'Quantity as − and +'), toggle('hideQuantity', 'Leave out the quantity'), toggle('priceInLabel', 'Show the price in the button'), { ...toggle('onlyLooks', 'A card\'s button always opens the quick view'), help: 'On a card only.' }, toggle('buyNow', 'Show a "Buy it now" button'), text('buyNowLabel', '"Buy it now" text'),
    { ...toggle('orderHere', 'Order with this page\'s form'), help: 'Shows only the choices and the quantity; the order form on the page takes the order.' },
    { ...toggle('iconOnly', 'A bag icon instead of the words'), help: 'For a product card\'s add button. A phone shows the bag either way.' }
  ],
  defaults: { props: { label: 'Add to cart', showOptions: false }, styles: { base: { default: {
    backgroundColor: '#1c3025', color: '#ffffff', paddingTop: '12px', paddingBottom: '12px',
    paddingLeft: '20px', paddingRight: '20px', borderWidth: '0px', borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', cursor: 'pointer'
  } } } }
});

// Checkout mounts the existing server-priced COD form inside a theme-owned section.
export const checkoutElement = element('checkout', {
  label: 'Checkout form', description: 'The secure order form for the selected product.', category: 'Other', icon: 'shopping-bag',
  props: [
    toggle('showHeading', 'Show the form\'s own heading'),
    { ...toggle('orderPageProduct', 'Order the product above when the cart is empty'), help: 'For a cash-on-delivery page: the form orders the product, option and quantity chosen in the same product block.' },
    toggle('sampleContent', 'Show theme samples in the editor')
  ],
  defaults: { props: { showHeading: true }, styles: {} }
});

// Confirmation reads only the order completed during this visit.
export const orderSummaryElement = element('order-summary', {
  label: 'Order confirmation', description: 'Shows the confirmation for a completed order.', category: 'Other', icon: 'check',
  props: [{ ...text('heading', 'Heading'), help: 'Write {name} where the buyer\'s first name goes.' }, toggle('showSteps', 'Show what happens next')],
  defaults: { props: { heading: 'Thank you, {name}!', showSteps: true }, styles: {} }
});

// An optional offer uses the server's eligible product and discount.
export const offerElement = element('offer', {
  label: 'Optional offer', description: 'A server-priced offer for the product in the cart.', category: 'Other', icon: 'gift',
  props: [
    toggle('alternative', 'Show an alternative offer'), text('badge', 'Badge'), text('acceptLabel', '"Yes" button text'),
    text('declineLabel', '"No" button text'), toggle('sampleContent', 'Show a theme sample in the editor')
  ],
  defaults: { props: { alternative: false, badge: 'Special offer', acceptLabel: 'Yes, add it to my order', declineLabel: 'No thanks' }, styles: {} }
});

// The store's social profiles, read from its settings, so the same addresses show in every theme.
export const socialLinksElement = element('social-links', {
  label: 'Social links', description: 'Icons linking to your social media, from Settings › General.', category: 'Other', icon: 'share-2',
  props: [num('size', 'Icon size', 16, 40)],
  defaults: { props: { size: 22 }, styles: look({ display: 'flex', gap: '4px', color: paint('heading') }) }
});

export const bundleElement = element('bundle', {
  label: 'Product bundle', description: 'Products chosen together, added to the cart in one step.', category: 'Other', icon: 'shopping-bag',
  props: [text('label', 'Button label')], defaults: { props: { label: 'Add all to cart' }, styles: {} }
});

// The path to the page being looked at — Home, then its collection, then the product — as Sleek prints it above both.
export const breadcrumbsElement = element('breadcrumbs', {
  label: 'Breadcrumbs', description: 'The way back from this page: Home, its collection, then the page itself.', category: 'Other', icon: 'list-tree',
  props: [text('homeLabel', 'First step'), text('separator', 'Between steps')],
  defaults: { props: { homeLabel: 'Home', separator: '›' }, styles: {} }
});

// The product's colours as round swatches, for a card or a product page, as Shopify's Modular prints them.
export const swatchesElement = element('swatches', {
  label: 'Colour swatches', description: 'The product\'s colours as round swatches, read from its colour option.', category: 'Other', icon: 'palette',
  props: [
    num('limit', 'Most shown', 1, 12),
    { ...toggle('pickable', 'Pressing a colour switches the card to it'), help: 'The card\'s picture, price and link follow, without leaving the page.' },
    named('shape', 'Shape', [['round', 'Round'], ['square', 'Square']]),
    named('showing', 'Shows', [['colours', 'Colours'], ['sizes', 'Other choices, such as sizes, as small links']])
  ],
  defaults: { props: { limit: 7 }, styles: look({ color: paint('heading') }) }
});

// Every picture of the product on the page, laid out the way Sleek's product page lays them.
export const productMediaElement = element('product-media', {
  label: 'Product pictures', description: 'All of the product\'s pictures: the first large and the rest two by two, or a grid.', category: 'Other', icon: 'image',
  props: [named('layout', 'Layout', [['stack', 'First large, then two by two'], ['grid', 'Two by two'], ['single', 'First picture only'], ['thumbs', 'One large over small ones']]), named('pictureShape', 'Picture shape', [['square', 'Square'], ['portrait', 'Tall']])],
  defaults: { props: { layout: 'stack' }, styles: {} }
});
